import { useMemo } from 'react';
import { useProperty } from '@vibesites/runtime/react';
import { apartmentSyncCss } from './apartment-sync-colors';

// Pala Mesa Brand Guidelines palette — used only when a dashboard field is
// empty or fails its contrast check.
const STUCCO_GRAY = '#A3ABAC';
const SLATE_TRIM = '#5F6A6C';
const INK = '#000000';
const TRIM_WHITE = '#E8E8E4';
const WHITE = '#FFFFFF';
// Borders that outline controls need 3:1: 4.64:1 on white, 3.78:1 on Trim White.
const CONTROL_BORDER = '#75756E';
// Placeholder and helper text in third-party widgets: 7.0:1 on white.
const MUTED_INK = '#595959';

function luminance(hex: string): number {
  const channels = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const [r, g, b] = channels.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

function toHex(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toUpperCase();
  if (/^#[0-9a-fA-F]{3}$/.test(v)) return `#${[...v.slice(1)].map((c) => c + c).join('')}`.toUpperCase();
  return null;
}

const warned = new Set<string>();

function pick(field: string, value: string | null, passes: (hex: string) => boolean, fallback: string, rule: string): string {
  if (value && passes(value)) return value;
  const message = `[site-colors] MultiHub "${field}" ${value ? `(${value}) fails: ${rule}` : 'is empty or not a hex color'}; using ${fallback}.`;
  if (!warned.has(message)) {
    warned.add(message);
    console.warn(message);
  }
  return fallback;
}

/**
 * Maps the MultiHub Colors fields onto site roles:
 * - Primary Color → header utility + nav band, which carries black text
 * - Secondary Color → buttons, links, footer and dark bands, which carry white text
 * - Font Color → body text
 * - Color 3 → light neutral section background
 */
export function resolveSiteColors(property: Record<string, unknown> | null | undefined) {
  const band = pick(
    'Primary Color',
    toHex(property?.property_primary_color),
    (hex) => contrast(hex, INK) >= 4.5,
    STUCCO_GRAY,
    'black header text needs 4.5:1',
  );
  const action = pick(
    'Secondary Color',
    toHex(property?.property_secondary_color),
    (hex) => contrast(hex, WHITE) >= 4.5,
    SLATE_TRIM,
    'white button/footer text needs 4.5:1',
  );
  // Body copy must read as black or deep charcoal (#1A1A1A is 17.4:1).
  const text = pick(
    'Font Color',
    toHex(property?.property_font_color),
    (hex) => contrast(hex, WHITE) >= 15,
    INK,
    'body text needs 15:1 on white',
  );
  const surface = pick(
    'Color 3',
    toHex(property?.property_color_3),
    (hex) => contrast(hex, INK) >= 4.5 && contrast(hex, action) >= 4.5,
    TRIM_WHITE,
    'black text and the link color each need 4.5:1 on it',
  );

  return { band, action, text, surface };
}

/** Resolved palette for components that need a color value rather than a CSS variable. */
export function useSiteColors() {
  const property = useProperty() as Record<string, unknown> | null;
  return useMemo(() => resolveSiteColors(property), [property]);
}

export function SiteColorSystem() {
  const colors = useSiteColors();

  return (
    <style>{`
      body {
        --site-color-band: ${colors.band};
        --site-color-on-band: ${INK};
        /* Inherited components use "primary" for buttons, links and dark
           bands with white text, so it carries the Secondary (action) color. */
        --vibe-color-primary: ${colors.action};
        --vibe-color-text-on-primary: ${WHITE};
        --vibe-color-secondary: ${colors.action};
        --vibe-color-accent: ${colors.action};
        --vibe-color-text-on-secondary: ${WHITE};
        --vibe-color-text: ${colors.text};
        --vibe-color-text-muted: ${colors.text};
        --vibe-color-background: ${WHITE};
        --vibe-color-surface: ${colors.surface};
        --vibe-color-border: ${CONTROL_BORDER};
      }

      /* Light context: small uppercase headings and labels use ink. */
      .uppercase.text-\\[var\\(--vibe-color-primary\\)\\] {
        color: var(--vibe-color-text);
      }

      /* Light context: links in editable text are bold, underlined Slate. */
      [data-vibesite-richtext] a {
        color: var(--vibe-color-primary);
        font-weight: 700;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
      [data-vibesite-richtext] a:hover {
        text-decoration-thickness: 2px;
      }

      /* Dark context: Slate bands, photo heroes and the header/footer keep
         links in the surrounding text color. */
      :is(
        [data-vibesite-slot='Header'],
        [data-vibesite-slot='Footer'],
        [data-vibesite-slot='SpecialOfferBanner'],
        [data-vibesite-slot='FloorplansList'],
        [data-vibesite-slot='ReviewsCallout'],
        [data-vibesite-slot='ScheduleATourCallout'],
        [data-vibesite-slot='CTASection'],
        [data-vibesite-slot$='Hero']
      ) [data-vibesite-richtext] a {
        color: inherit;
      }

      /* Hero CTA hover darkens instead of lightening (white on Slate
         lightened 10% is only 4.47:1). */
      body .hero-cta:hover {
        background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 75%, black) 100%);
      }

      /* Promo banner: translucent white falls under AA on Slate. */
      body .sob-disclaimer {
        color: color-mix(in srgb, var(--vibe-color-text-on-primary) 85%, transparent);
      }
      body .sob-dot {
        border-color: color-mix(in srgb, var(--vibe-color-text-on-primary) 80%, transparent);
      }
      body .sob-close {
        color: var(--vibe-color-text-on-primary);
        border-color: color-mix(in srgb, var(--vibe-color-text-on-primary) 70%, transparent);
      }

      /* Floor plans band: eyebrow and subtitle are solid white on Slate
         (80% white is 4.25:1). */
      body .fp-text-muted {
        color: var(--vibe-color-text-on-primary);
      }

      ${apartmentSyncCss}

      /* Lead form and tour scheduler: placeholders, the move-in date overlay
         and dropdown chevrons ship #9CA3AF (2.54:1 on white). */
      form ::placeholder {
        color: ${MUTED_INK};
        opacity: 1;
      }
      .mh-lead-form-date-field > span,
      .mh-lead-form-card select + svg,
      .mh-lead-form-date-field svg,
      form .text-gray-400 {
        color: ${MUTED_INK} !important;
      }
      .mh-lead-form-card select:invalid {
        color: ${MUTED_INK} !important;
      }

      /* Keyboard focus: black ring on light surfaces and the Stucco header,
         white ring inside Slate bands. */
      a:focus-visible,
      button:focus-visible {
        outline: 2px solid ${INK};
        outline-offset: 2px;
      }
      :is(.footer-main, .sob) :is(a, button):focus-visible {
        outline-color: ${WHITE};
      }

      /* RentCafe chat launcher: the vendor tints it with Primary (Stucco)
         under a white icon, so repaint it Slate. */
      #conversations-webchat-toggle,
      .rcc-app-cta-wrap button {
        background-color: var(--vibe-color-primary) !important;
        color: ${WHITE} !important;
      }
    `}</style>
  );
}
